import { CookedProduct, CookedProductList, isPricedProduct } from '@apps/registry/common/selectors/ProductListSelector';
import { ECard, ShippingAddressFragment, useCreateRegistryPaymentIntentMutation, useGetCalculateRegistryPurchaseInfoQuery } from '@graphql/generated';
import { createContext } from '@shared/utils/createContext';
import { GuestRegistryState } from '@apps/registry/guest/GuestRegistry.controller';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GiftWrapOfferState } from '@apps/registry/guest/components/CheckoutDialog/components/GiftWrapOffer/GiftWrapOffer';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { prepareECardPayload, useGiftWrapECardPrice, useSaveCardDraft } from '@shared/components';
import { LineItem, StripeExpressCheckoutElementClickEvent, StripeExpressCheckoutElementConfirmEvent, StripeExpressCheckoutElementReadyEvent } from '@stripe/stripe-js';
import { GiftWrapType } from '../ShoppingCart/ShoppingCart.telemetry';
import { useRoutePaths } from '@apps/registry/guest/GuestRegistry.routes';
import { useHistory, useRouterHelper, useTranslation } from '@shared/core';
import { withWindow } from '@shared/utils/withWindow';
import { useFullPageLoader } from '@shared/components/JoyLogoLoader/FullPageLoaderProvider';
import { useGuestRegistryTelemetry } from '@apps/registry/guest/GuestRegistry.telemetry';
import { ExpressCheckoutPurchaseItem } from './ExpressCheckout';
import { interpretShippingDecimalString, shippingAddressFragmentAsInput } from '../../utils/shippingAddress';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { isSomePaymentMethodAvailable } from '@shared/core/stripe';
import { deriveAggregateCheckoutCodesResult } from '../../utils/checkoutMechanisms';
import { composePurchaseReturnUrlTemplate } from '@apps/registry/guest/hooks/usePurchaseConfirmationLink';
import { AdditionalCostLine, additionalCostLinesInDisplayOrder } from '../../utils/additionalCostLines';
import { useIsFeatureEnabled } from '@shared/core/featureFlags/featureFlags.provider';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { getGiftWrapThemeListByEventType } from '@shared/components';
import { canShowGW } from '@apps/registry/guest/components/CheckoutDialog/hooks/useCheckoutAllowGWByLocation/utils';
import { isBillingAddressValid } from '@apps/registry/guest/components/CheckoutDialog/hooks/useCheckoutAllowGWByLocation/useCheckoutAllowGWByLocation';

export interface MonetaryValue {
  valueInMinorUnits: number;
}

export enum ExpressCheckoutShippingOption {
  CoupleAddress = 'CoupleAddress',
  ShipToMe = 'ShipToMe',
}

export enum InternalCheckoutErrorCode {
  serverError = 'server_error',
  missingClientSecret = 'missing_client_secret',
  missingConfirmationToken = 'missing_confirmation_token',
  insufficientData = 'insufficient_data',
  unknownError = 'unknown_error',
  forbiddenShippingCountry = 'forbidden_shipping_country',
}

const countryAlpha2toAlpha3: Record<string, string> = {
  US: 'USA',
};

export interface CheckoutError {
  readonly code?: InternalCheckoutErrorCode | string;
  readonly message?: string;
}

export type ProductWithQuantity = CookedProduct & { quantity: number; totalValueInMinorUnits: number };

interface State {
  purchaseProducts: ProductWithQuantity[];
  productsPrice: MonetaryValue;
  hasPhysicalProducts: boolean;
  isGiftWrapEnabled: boolean;
  totalPrice: MonetaryValue;
  currencyCode: string;
  shippingOption: ExpressCheckoutShippingOption;
  /** Placeholder for giftwrap message, which will be replaced with user name after purchase */
  messageGiverNamePlaceholder: string;

  checkoutError?: CheckoutError | null;
  eCardPrice?: MonetaryValue | null;
  giftWrap?: GiftWrapOfferState | null;
  shippingAndHandlingCost?: MonetaryValue | null;
  additionalCostLines: Array<AdditionalCostLine>;
  coupleAddress?: ShippingAddressFragment | null;
  supportsShipToMe: boolean;
}

interface Mutators {
  setGiftWrap: (giftWrap: GiftWrapOfferState) => void;
  setShippingOption: (option: ExpressCheckoutShippingOption) => void;
  handleCheckoutClick: (event: StripeExpressCheckoutElementClickEvent) => void;
  handleCheckoutConfirm: (event: StripeExpressCheckoutElementConfirmEvent) => void;
  handleCheckoutReady: (event: StripeExpressCheckoutElementReadyEvent) => void;
}

interface ExpressCheckoutProviderProps {
  eventHandle: string;
  eventId: string;
  purchaseItems: ExpressCheckoutPurchaseItem[];
  registryState: GuestRegistryState;
  allProducts: CookedProductList;
}

interface ExpressCheckoutContext {
  state: State;
  mutators: Mutators;
}

// Express checkout only supports US shipping (see allowedShippingCountries),
// so a small mapping is sufficient and avoids Intl.DisplayNames compatibility issues.
const countryAlpha2ToName: Record<string, string> = {
  US: 'United States',
};

export const [Provider, useExpressCheckoutContext] = createContext<ExpressCheckoutContext>({ name: 'ExpressCheckout' });

export const ExpressCheckoutProvider: React.FC<ExpressCheckoutProviderProps> = ({ eventHandle, eventId, purchaseItems, registryState, allProducts, children }) => {
  const coupleAddress = registryState.shippingAddress;
  const elements = useElements();
  const telemetry = useGuestRegistryTelemetry();
  const [isClientSidePaymentConfirmationEnabled] = useIsFeatureEnabled('registryClientSidePaymentConfirmationEnabled');
  const { eventInfo } = useEventInfo();
  const { value: registryGiftWrapCashOn } = useFeatureValue('registryGiftWrapCashEnabled');
  const { value: registryGiftWrapExperimentOn } = useFeatureValue('registryGiftWrapExperimentEnabled');
  const { value: registryGiftWrapUseEventAddressOn } = useFeatureValue('registryGiftWrapUseEventAddress');
  const { value: registryGiftWrapDefaultUSABillingAddress, payload: registryGiftWrapDefaultUSABillingAddressPayload } = useFeatureValue('registryGiftWrapDefaultUSABillingAddress');
  const stripe = useStripe();
  const routes = useRoutePaths();
  const router = useRouterHelper();
  const history = useHistory();
  const [shippingOption, setShippingOption] = useState(coupleAddress ? ExpressCheckoutShippingOption.CoupleAddress : ExpressCheckoutShippingOption.ShipToMe);
  const [giftWrap, setGiftWrap] = useState<GiftWrapOfferState | null>(null);
  const [createRegistryPaymentIntentMutation] = useCreateRegistryPaymentIntentMutation();
  const { t2 } = useTranslation('sharedRegistry');
  const trans = t2('shoppingCart');
  const messageGiverNamePlaceholder = trans.messageStep.giverNamePlaceholder;
  const giftWrapPrice = useGiftWrapECardPrice();
  const eCardPrice = useMemo(() => ({ valueInMinorUnits: giftWrapPrice * 100 }), [giftWrapPrice]);
  const [saveCardDraft] = useSaveCardDraft();
  const setFullPageLoading = useFullPageLoader({ key: 'ExpressCheckout', initialIsLoading: false });
  const [checkoutError, setCheckoutError] = useState<CheckoutError | null>(null);

  const purchaseProducts = useMemo(() => {
    const purchaseItemsMap = new Map(purchaseItems.map(item => [item.registryItemId, item]));
    return allProducts.products.reduce<ProductWithQuantity[]>((acc, product) => {
      const purchaseItem = purchaseItemsMap.get(product.registryItemId);
      if (!purchaseItem) {
        return acc;
      }

      if (product.donationFund) {
        const quantity = purchaseItem.quantity || product.donationFund.remaining * 100 || product.donationFund.goal * 100 || 1_000_00;
        acc.push({ ...product, quantity, totalValueInMinorUnits: quantity });
      } else if (isPricedProduct(product)) {
        const quantity = Math.min(product.stillNeeded, purchaseItem.quantity || product.stillNeeded);
        acc.push({ ...product, quantity, totalValueInMinorUnits: product.valueInMinorUnits * quantity });
      }

      return acc;
    }, []);
  }, [allProducts, purchaseItems]);
  const hasPhysicalProducts = useMemo(() => purchaseProducts.some(product => product.registryItemType === 'product'), [purchaseProducts]);
  const themes = getGiftWrapThemeListByEventType(eventInfo?.eventType);
  const eventTypeHasThemes = !!themes?.length;
  // Treat gift wrap as cash-fund-only when all items are donation funds.
  const isCashFundOnlyPurchase = purchaseProducts.length > 0 && purchaseProducts.every(product => !!product.donationFund);
  const isGiftWrapAllowedByLocation = useMemo(() => {
    const { allow } = canShowGW(
      registryState?.shippingAddress,
      registryGiftWrapUseEventAddressOn ? eventInfo?.locationInfo : undefined,
      registryGiftWrapDefaultUSABillingAddress && isBillingAddressValid(registryGiftWrapDefaultUSABillingAddressPayload)
        ? registryGiftWrapDefaultUSABillingAddressPayload.address
        : undefined,
    );
    return allow;
  }, [
    eventInfo?.locationInfo,
    registryGiftWrapUseEventAddressOn,
    registryGiftWrapDefaultUSABillingAddress,
    registryGiftWrapDefaultUSABillingAddressPayload,
    registryState?.shippingAddress,
  ]);
  const isGiftWrapEnabled = useMemo(() => {
    if (!isGiftWrapAllowedByLocation || !purchaseProducts.length) {
      return false;
    }
    if (isCashFundOnlyPurchase) {
      return registryGiftWrapCashOn === 'on';
    }
    return registryGiftWrapExperimentOn === 'on' && eventTypeHasThemes;
  }, [eventTypeHasThemes, isCashFundOnlyPurchase, isGiftWrapAllowedByLocation, purchaseProducts.length, registryGiftWrapCashOn, registryGiftWrapExperimentOn]);

  const lineItems = useMemo(() => purchaseProducts?.map(product => ({ registryItemId: product.registryItemId, quantity: product.quantity })), [purchaseProducts]);

  const shippingAddress = shippingAddressFragmentAsInput(coupleAddress);
  const getCalculateRegistryPurchaseInfoQuery = useGetCalculateRegistryPurchaseInfoQuery({
    variables: {
      payload: {
        eventId: eventId,
        address: shippingAddress,
        lineItems,
      },
    },
    skip: !shippingAddress || !lineItems.length,
    batchMode: 'fast',
  });
  const calculateRegistryPurchaseInfo = getCalculateRegistryPurchaseInfoQuery.data?.calculateRegistryPurchaseInfo;
  const shippingAndHandlingCost = calculateRegistryPurchaseInfo?.shippingAndHandlingTotal;
  const { isFreeShipping } = interpretShippingDecimalString(calculateRegistryPurchaseInfo?.shippingAndHandlingTotal?.floatingPointDecimalString);
  // Shipping is *NOT* treated as a special case here; it is "just another cost line" to be rendered
  const additionalCostLines = additionalCostLinesInDisplayOrder(calculateRegistryPurchaseInfo, { includeShipping: true });

  const { isLowInventory } = deriveAggregateCheckoutCodesResult([calculateRegistryPurchaseInfo]) ?? {};

  const productsPrice = useMemo<MonetaryValue>(
    () => ({ valueInMinorUnits: purchaseProducts.reduce((acc, product) => acc + product.totalValueInMinorUnits, 0) }),
    [purchaseProducts],
  );
  const currencyCode = useMemo(() => purchaseProducts.at(0)?.currencyCode ?? registryState.registryCurrencyCode ?? DEFAULT_CURRENCY, [purchaseProducts, registryState]);
  const supportsShipToMe = !isLowInventory;

  const totalPrice = useMemo<MonetaryValue>(() => {
    let totalPriceInMinorUnits = productsPrice.valueInMinorUnits;
    additionalCostLines.forEach(line => {
      totalPriceInMinorUnits += line.lineValue?.valueInMinorUnits ?? 0;
    });
    if (giftWrap?.wrapOption === 'eCard' && eCardPrice) {
      totalPriceInMinorUnits += eCardPrice.valueInMinorUnits;
    }
    return { valueInMinorUnits: totalPriceInMinorUnits };
  }, [productsPrice, giftWrap, eCardPrice, additionalCostLines]);

  useEffect(() => {
    if (!isGiftWrapEnabled && giftWrap) {
      setGiftWrap(null);
    }
  }, [isGiftWrapEnabled, giftWrap]);

  useEffect(() => {
    if (!elements || !totalPrice.valueInMinorUnits) {
      return;
    }

    elements.update({
      amount: totalPrice.valueInMinorUnits,
      currency: currencyCode.toLowerCase(),
      mode: 'payment',
    });
  }, [elements, totalPrice, currencyCode, registryState]);

  const createECard = useCallback(
    async ({ message, themeId }: Pick<GiftWrapOfferState, 'message' | 'themeId'>): Promise<ECard | null> => {
      const product = purchaseProducts.at(0);
      if (!product) {
        return null;
      }

      let productLink;
      try {
        const origin = window?.location?.origin ?? 'https://withjoy.com';
        productLink = new URL(`${eventHandle}/edit/registry/track`, origin)?.href;
      } catch (e) {
        // Ignore invalid URL construction; fallback to empty gift link.
      }

      const payload = {
        message,
        themeId,
        image: product.image,
        title: product.title,
        giftLink: productLink ?? '',
      };
      const draftId = await saveCardDraft(eventId, payload);

      if (typeof draftId === 'string') {
        return prepareECardPayload({
          price: eCardPrice.valueInMinorUnits / 100,
          draftId,
          theme: themeId,
          imageURL: product.image,
          offerType: GiftWrapType.CART,
        });
      }
      return null;
    },
    [eCardPrice, purchaseProducts, saveCardDraft, eventHandle, eventId],
  );

  const handleCheckoutConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      const handleError = (error: CheckoutError) => {
        telemetry.checkoutError(error);
        setCheckoutError(error);
        event.paymentFailed({ reason: 'fail' });
      };
      if (!stripe || !elements || !lineItems.length || !event.billingDetails?.email) {
        return handleError({ code: InternalCheckoutErrorCode.insufficientData });
      }
      if (isGiftWrapEnabled && !giftWrap) {
        return handleError({ code: InternalCheckoutErrorCode.insufficientData });
      }

      if (event.shippingAddress && event.shippingAddress.address.country !== 'US') {
        return handleError({ code: InternalCheckoutErrorCode.forbiddenShippingCountry });
      }

      try {
        setFullPageLoading(true);

        const giverName = event.shippingAddress?.name ?? event.billingDetails.name;
        const giftMessage = isGiftWrapEnabled && giftWrap ? giftWrap.message.replace(messageGiverNamePlaceholder, giverName) : '';
        let eCard: ECard | null = null;
        if (isGiftWrapEnabled && giftWrap?.wrapOption === 'eCard') {
          eCard = await createECard({ themeId: giftWrap.themeId, message: giftMessage });
        }

        const { error: submitError } = await elements.submit();
        if (submitError) {
          return handleError(submitError);
        }

        let confirmationTokenId: string | undefined = undefined;
        if (!isClientSidePaymentConfirmationEnabled) {
          const { confirmationToken, error: tokenError } = await stripe.createConfirmationToken({ elements });
          if (tokenError) {
            return handleError(tokenError);
          }

          if (!confirmationToken.id) {
            return handleError({ code: InternalCheckoutErrorCode.missingConfirmationToken });
          }
          confirmationTokenId = confirmationToken.id;
        }

        const returnUrlRoute = withWindow(global => global.location.origin + routes.guestRegistry.path + '/purchase', '');
        const returnUrlTemplate = composePurchaseReturnUrlTemplate(returnUrlRoute);
        let paymentIntentResult: Awaited<ReturnType<typeof createRegistryPaymentIntentMutation>>;
        try {
          paymentIntentResult = await createRegistryPaymentIntentMutation({
            variables: {
              payload: {
                eventId,
                name: giverName,
                email: event.billingDetails.email,
                note: giftMessage || undefined,
                lineItems,
                returnUrlTemplate,
                confirmationTokenId,
                eCard,
                address:
                  event.shippingAddress && shippingOption === ExpressCheckoutShippingOption.ShipToMe
                    ? {
                        name: event.shippingAddress.name,
                        address1: event.shippingAddress.address.line1,
                        address2: event.shippingAddress.address.line2,
                        city: event.shippingAddress.address.city,
                        countryCode: countryAlpha2toAlpha3[event.shippingAddress.address.country],
                        country: countryAlpha2ToName[event.shippingAddress.address.country] ?? event.shippingAddress.address.country,
                        postalCode: event.shippingAddress.address.postal_code,
                        state: event.shippingAddress.address.state,
                        phone: event.billingDetails.phone,
                      }
                    : null,
              },
            },
          });
        } catch (ex) {
          return handleError({ code: InternalCheckoutErrorCode.serverError });
        }

        const paymentIntent = paymentIntentResult.data?.createRegistryPaymentIntent;
        if (paymentIntent?.__typename !== 'RegistryPaymentIntent') {
          const code = (paymentIntent && 'checkoutFailureCode' in paymentIntent && paymentIntent.checkoutFailureCode) || InternalCheckoutErrorCode.serverError;
          return handleError({ code });
        }

        if (!paymentIntent.clientSecret) {
          return handleError({ code: InternalCheckoutErrorCode.missingClientSecret });
        }

        if (isClientSidePaymentConfirmationEnabled) {
          const { error } = await stripe.confirmPayment({
            elements,
            clientSecret: paymentIntent.clientSecret,
            confirmParams: {
              return_url: paymentIntent.returnUrl,
            },
            redirect: 'if_required',
          });
          if (error) {
            return handleError(error);
          }
        } else {
          if (paymentIntent.status === 'requires_action') {
            await stripe.handleNextAction({
              clientSecret: paymentIntent.clientSecret,
            });
          }
        }

        telemetry.checkoutSuccess({
          giftWrapOption: giftWrap?.wrapOption ?? 'giftNote',
          shippingOption,
          lineItems,
          productsPriceInMinorUnits: productsPrice.valueInMinorUnits,
          totalPriceInMinorUnits: totalPrice.valueInMinorUnits,
          expressPaymentType: event.expressPaymentType,
        });
        const returnUrl = new URL(paymentIntent.returnUrl);
        history.replace(returnUrl.pathname + returnUrl.search);
      } catch (ex) {
        handleError({ code: InternalCheckoutErrorCode.unknownError });
      } finally {
        setFullPageLoading(false);
      }
    },
    [
      stripe,
      elements,
      giftWrap,
      shippingOption,
      routes,
      createECard,
      createRegistryPaymentIntentMutation,
      eventId,
      setFullPageLoading,
      history,
      setCheckoutError,
      telemetry,
      totalPrice,
      productsPrice,
      lineItems,
      messageGiverNamePlaceholder,
      isClientSidePaymentConfirmationEnabled,
      isGiftWrapEnabled,
    ],
  );

  /**
   * compose the Stripe Checkout payload for an Express Checkout
   *   its "standard" Checkout equivalent is in `registry_service` + 'CommerceHelper.createCommerceOrder'
   */
  const handleCheckoutClick = useCallback(
    (event: StripeExpressCheckoutElementClickEvent) => {
      telemetry.expressCheckoutClicked({ ...event, source: 'CheckoutPage' });
      const stripeLineItems: LineItem[] = purchaseProducts.map(product => ({ name: `${product.quantity} x ${product.title}`, amount: product.totalValueInMinorUnits }));
      if (giftWrap?.wrapOption === 'eCard') {
        stripeLineItems.push({ name: 'Personalized eCard', amount: eCardPrice.valueInMinorUnits });
      }
      additionalCostLines.forEach(line => {
        stripeLineItems.push({ name: line.title, amount: line.lineValue?.valueInMinorUnits ?? 0 });
      });
      event.resolve({
        allowedShippingCountries: ['US'],
        billingAddressRequired: true,
        emailRequired: true,
        lineItems: stripeLineItems,
        ...(shippingOption === ExpressCheckoutShippingOption.ShipToMe
          ? {
              shippingAddressRequired: true,
              phoneNumberRequired: true,
              shippingRates: [
                {
                  id: 'default',
                  displayName: isFreeShipping ? trans.shippingAddressStep.freeShipping : trans.shippingAddressStep.standardShipping,
                  amount: shippingAndHandlingCost?.valueInMinorUnits ?? 0,
                },
              ],
            }
          : {}),
      });
    },
    [shippingOption, purchaseProducts, trans, shippingAndHandlingCost, giftWrap, eCardPrice, telemetry, additionalCostLines, isFreeShipping],
  );

  const handleCheckoutReady = useCallback(
    (event: StripeExpressCheckoutElementReadyEvent) => {
      if (event.availablePaymentMethods && isSomePaymentMethodAvailable(event.availablePaymentMethods)) {
        return;
      }
      // redirect back to registry if no available payment methods
      history.replace(router.buildPath(''));
    },
    [history, router],
  );

  useEffect(() => {
    if (purchaseProducts.length) {
      return;
    }
    // redirect back to registry if no items to purchase
    history.replace(router.buildPath(''));
  }, [purchaseProducts, history, router]);

  return (
    <Provider
      value={{
        state: {
          purchaseProducts,
          hasPhysicalProducts,
          isGiftWrapEnabled,
          currencyCode,
          giftWrap,
          shippingAndHandlingCost,
          additionalCostLines,
          shippingOption,
          coupleAddress,
          supportsShipToMe,
          productsPrice,
          totalPrice,
          eCardPrice,
          checkoutError,
          messageGiverNamePlaceholder,
        },
        mutators: {
          setShippingOption,
          setGiftWrap,
          handleCheckoutClick,
          handleCheckoutConfirm,
          handleCheckoutReady,
        },
      }}
    >
      {children}
    </Provider>
  );
};
